import React, { useEffect, useRef, useState } from "react";
import { Move, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { focalPosition, DEFAULT_FOCAL } from "@/components/featured/FeaturedProCard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const clamp = (v) => Math.min(1, Math.max(0, v));
const PREVIEWS = [{ k: "card", label: "Featured card (3:2) · Homepage, Connections & Marketplace", cls: "aspect-[3/2]" }];

function Frame({ src, focal, cls, children, ...rest }) {
  return (
    <div className={`relative ${cls} w-full overflow-hidden rounded-xl bg-[#0F2C59]`} {...rest}>
      <img src={src} alt="" draggable={false} className="pointer-events-none h-full w-full select-none object-cover" style={{ objectPosition: focalPosition(focal) }} />
      {children}
    </div>
  );
}

// Drag-to-position framing for the Featured Professional card portrait. Original photo is never altered.
export function PortraitFramingDialog({ open, onOpenChange, src, initial, onSaved }) {
  const [focal, setFocal] = useState(initial || DEFAULT_FOCAL);
  const [saving, setSaving] = useState(false);
  const drag = useRef(null);
  useEffect(() => { if (open) setFocal(initial || DEFAULT_FOCAL); }, [open, initial]);

  const onDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, f: focal, w: e.currentTarget.clientWidth, h: e.currentTarget.clientHeight };
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    setFocal({ x: clamp(d.f.x - (e.clientX - d.x) / d.w), y: clamp(d.f.y - (e.clientY - d.y) / d.h) });
  };
  const onKey = (e) => {
    const step = { ArrowLeft: [-0.05, 0], ArrowRight: [0.05, 0], ArrowUp: [0, -0.05], ArrowDown: [0, 0.05] }[e.key];
    if (!step) return;
    e.preventDefault();
    setFocal((f) => ({ x: clamp(f.x + step[0]), y: clamp(f.y + step[1]) }));
  };

  const persist = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/avatar-focal", body);
      toast.success(body ? "Photo framing saved" : "Photo framing reset");
      onSaved?.(data.avatar_focal);
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="portrait-framing-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-[#0F2C59]">Adjust photo framing</DialogTitle>
          <DialogDescription>Drag the photo to keep your face clearly visible on Featured Professional cards. Your original photo is not changed.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_180px]">
          <Frame src={src} focal={focal} cls="aspect-[3/2] cursor-grab touch-none active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B]"
            data-testid="portrait-framing-stage" tabIndex={0} role="slider" aria-label="Photo position (use arrow keys)"
            aria-valuetext={`${Math.round(focal.x * 100)}% horizontal, ${Math.round(focal.y * 100)}% vertical`}
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }} onKeyDown={onKey}>
            <span className="pointer-events-none absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-[11px] font-medium text-white"><Move className="h-3 w-3" /> Drag to reposition</span>
          </Frame>
          <div className="space-y-3" data-testid="portrait-framing-previews">
            {PREVIEWS.map((p) => (
              <div key={p.k}>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{p.label}</p>
                <Frame src={src} focal={focal} cls={p.cls} data-testid={`portrait-preview-${p.k}`} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button data-testid="portrait-framing-reset" variant="ghost" disabled={saving} onClick={() => persist(null)} className="rounded-xl text-slate-600">
            <RotateCcw className="mr-1.5 h-4 w-4" /> Reset to default
          </Button>
          <div className="flex gap-2">
            <Button data-testid="portrait-framing-cancel" variant="outline" disabled={saving} onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="portrait-framing-save" disabled={saving} onClick={() => persist(focal)} className="rounded-xl bg-[#0F2C59] hover:bg-[#143163]">Save framing</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PortraitFramingDialog;
